import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Shield, Calendar as CalIcon, FileText, LogIn } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const lookup = async (e) => {
    e.preventDefault();
    if (!phone) return;
    try {
      const res = await api.get("/warranty/lookup", { params: { phone } });
      setData(res.data);
    } catch {}
  };

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-zinc-400" data-testid="dashboard-loading">Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="dashboard-page">
      <div className="overline text-[#FFB800]">Welcome</div>
      <h1 className="font-display text-4xl mt-2">Hi {user.name?.split(" ")[0] || "there"}.</h1>
      <p className="text-zinc-400 text-sm mt-3">Your quotes, bookings, warranty and live jobs — all in one place.</p>

      <div className="grid md:grid-cols-3 gap-5 mt-10">
        <Link to="/quote" className="card-surface rounded-lg p-6" data-testid="dash-card-quote">
          <FileText className="h-5 w-5 text-[#FFB800]" />
          <div className="font-display text-lg mt-4">Get a quote</div>
          <div className="text-sm text-zinc-400 mt-1">Price your next protection package.</div>
        </Link>
        <Link to="/booking" className="card-surface rounded-lg p-6" data-testid="dash-card-book">
          <CalIcon className="h-5 w-5 text-[#FFB800]" />
          <div className="font-display text-lg mt-4">Book a service</div>
          <div className="text-sm text-zinc-400 mt-1">Reserve a slot at the studio.</div>
        </Link>
        <Link to="/warranty" className="card-surface rounded-lg p-6" data-testid="dash-card-warranty">
          <Shield className="h-5 w-5 text-[#FFB800]" />
          <div className="font-display text-lg mt-4">Warranty & history</div>
          <div className="text-sm text-zinc-400 mt-1">Look up past jobs and coverage.</div>
        </Link>
      </div>

      <div className="card-surface rounded-lg p-6 mt-10" data-testid="dash-history">
        <div className="overline mb-3">Your service history</div>
        <form onSubmit={lookup} className="flex gap-2 max-w-sm">
          <Input placeholder="Phone used at booking" value={phone} onChange={(e) => setPhone(e.target.value)}
            className="bg-[#0A0A0C] border-zinc-800 h-10" data-testid="dash-phone" />
          <button className="btn-outline text-sm" type="submit" data-testid="dash-lookup">Load</button>
        </form>
        {data && (
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div>
              <div className="overline mb-2">Bookings</div>
              {data.bookings.length === 0 ? <div className="text-sm text-zinc-500">None yet.</div> :
                <ul className="text-sm divide-y divide-zinc-900">
                  {data.bookings.map((b) => (
                    <li key={b.id} className="py-2 flex justify-between">
                      <span>{b.service_name} · {b.car_make} {b.car_model}</span>
                      <span className="text-xs text-zinc-500">{b.date}</span>
                    </li>
                  ))}
                </ul>}
            </div>
            <div>
              <div className="overline mb-2">Live jobs</div>
              {data.jobs.length === 0 ? <div className="text-sm text-zinc-500">No active jobs.</div> :
                <ul className="text-sm divide-y divide-zinc-900">
                  {data.jobs.map((j) => (
                    <li key={j.id} className="py-2 flex justify-between">
                      <Link to={`/track/${j.id}`} className="hover:text-[#FFB800] font-mono text-xs">{j.id}</Link>
                      <span className="text-xs text-[#FFB800]">{j.stage}</span>
                    </li>
                  ))}
                </ul>}
            </div>
          </div>
        )}
      </div>

      {user.role === "admin" && (
        <div className="mt-8">
          <Link to="/admin" className="btn-outline" data-testid="dash-admin-link"><LogIn className="h-4 w-4" /> Open Admin CRM</Link>
        </div>
      )}
    </div>
  );
}
